// Workspace — one screen over two modules. The Notifications tab is gated by
// `inbox`, the Calendar tab by `calendar`; the route, nav item and home page open
// this screen if either tab would show (WORKSPACE_MODULES in lib/roles.ts).
//
// The tab lives in `?tab=` rather than component state or localStorage, so a
// link (the old /inbox route, and notifications pointing at the calendar) can
// name the tab it lands on.
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { can, canOpen, type PermLevel } from '../lib/roles';
import type { AttentionCounts } from '../lib/attention';
import { AttentionCount, Tabs, type IconName } from '../components/primitives';
import { usePageCrumbs } from '../components/shell';
import { NotificationsTab } from '../components/notifications';
import { CalendarTab } from '../components/calendar';
import { ImportsTab } from '../components/googleImports';

type Tab = 'notifications' | 'calendar' | 'imports';

// `min`: the level the tab needs beyond opening the module at all. UX only —
// google_imports() and the sync function check calendar:full themselves.
const TABS: { id: Tab; label: string; icon: IconName; module: string; min?: PermLevel }[] = [
  { id: 'notifications', label: 'Notifications', icon: 'inbox', module: 'inbox' },
  { id: 'calendar', label: 'Calendar', icon: 'calendar', module: 'calendar' },
  { id: 'imports', label: 'Google imports', icon: 'download', module: 'calendar', min: 'full' },
];

// A Record so a tab added without a subtitle fails to compile.
const TAB_SUBTITLE: Record<Tab, string> = {
  notifications: 'Bug reports, leave requests, approvals and events that concern you',
  calendar: 'Events, holidays and working days across the month',
  imports: 'Events pulled from the company Google Calendar',
};

export function WorkspaceScreen({ attention }: { attention: AttentionCounts }) {
  const { perms } = useAuth();
  const [params, setParams] = useSearchParams();
  const tabs = TABS.filter(
    (t) => canOpen(perms, t.module) && (!t.min || can(perms, t.module, t.min)),
  );
  // Checked against the tabs this person has: a stale or hand-typed ?tab= falls
  // back to the first one rather than highlighting nothing over an empty body
  // (Org Settings restores its tab without this check, and has that bug).
  const activeTab = tabs.find((t) => t.id === params.get('tab')) ?? tabs[0];
  const tab = activeTab?.id;

  // The one sub-crumb this screen ever has: which section is open.
  usePageCrumbs(activeTab ? [{ label: activeTab.label }] : []);

  return (
    <div
      style={{
        padding: 'var(--hf-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-4)',
      }}
    >
      {/* Wraps: beside the tabs on a phone the subtitle stacked one word per
          line (WS-05). Short of room it takes the next line whole. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--hf-space-2) var(--hf-space-3)',
        }}
      >
        <Tabs
          label="Workspace sections"
          tabs={tabs.map((t) =>
            t.id === 'notifications'
              ? {
                  ...t,
                  // The same object App hands the sidebar, so the Workspace badge
                  // and this one cannot disagree. docs/UI-CONVENTIONS.md §1.
                  badge: (
                    <AttentionCount
                      n={attention.byQueue.notifications ?? 0}
                      label="notifications awaiting you"
                    />
                  ),
                }
              : t,
          )}
          value={tab}
          onChange={(id) => setParams({ tab: id }, { replace: true })}
        />
        {tab && (
          <span
            style={{ flex: '1 1 16rem', fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}
          >
            {TAB_SUBTITLE[tab]}
          </span>
        )}
      </div>

      {tab === 'notifications' && <NotificationsTab onUnreadChange={attention.reload} />}
      {tab === 'calendar' && <CalendarTab />}
      {tab === 'imports' && <ImportsTab />}
    </div>
  );
}
